<template>
  <card-dialog
    v-model="modalVisible"
    max-width="700px"
    title="Marketing Campaign"
  >
    <v-card-text
      class="pa-6"
    >
      <div class="title mb-2">
        <v-skeleton-loader v-if="loading" />
        <span v-else-if="playlist">
          {{ playlist.title }}
        </span>
      </div>
      <v-card
        outlined
        flat
      >
        <v-skeleton-loader v-if="loading" />
        <v-card-text v-else-if="playlist">
          {{ playlist.description || 'No description' }}
        </v-card-text>
      </v-card>

      <div class="mb-2 mt-6 font-weight-bold">
        Advertisements
      </div>
      <v-card
        outlined
        flat
      >
        <v-skeleton-loader
          v-if="loading"
          type="list"
        />
        <v-list
          v-else-if="playlist"
          dense
          class="ma-0 pa-0"
        >
          <v-list-item
            v-for="file in playlist.library_files"
            :key="file.id"
            class="list-element--bordered"
          >
            <v-list-item-title>
              {{ file.original_file }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card>

      <v-row
        no-gutters
        class="mb-2 mt-6"
      >
        <v-col>
          <div class="font-weight-bold">
            Active Dates
          </div>
        </v-col>
        <v-col
          v-if="playlist"
          cols="auto"
        >
          <v-chip
            small
            class="ma-0"
            :color="playlist.is_active ? 'secondary' : 'grey lighten-2'"
          >
            {{ playlist.is_active ? 'Currently Active' : 'Currently Inactive' }}
          </v-chip>
        </v-col>
      </v-row>

      <v-card
        outlined
        flat
      >
        <v-skeleton-loader
          v-if="loading"
          type="text"
        />
        <v-card-text v-else-if="playlist">
          <date-range-label
            :from="playlist.active_from"
            :until="playlist.active_until"
          />
        </v-card-text>
      </v-card>

      <div class="mb-2 mt-6 font-weight-bold">
        Radios Included In
      </div>
      <v-card
        outlined
        flat
      >
        <v-skeleton-loader
          v-if="loading"
          type="list"
        />
        <v-list
          v-else-if="playlist && playlist.radios.length > 0"
          dense
          class="ma-0 pa-0"
        >
          <v-list-item
            v-for="radio in playlist.radios"
            :key="radio.id"
            class="list-element--bordered"
            :to="{ name: 'radio', params: { id: radio.id }}"
            target="_blank"
          >
            <v-list-item-title>
              {{ radio.title }}
            </v-list-item-title>
            <v-list-item-action>
              <v-icon small>
                fa fa-arrow-right
              </v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <div
          v-else
          class="pa-2"
        >
          None
        </div>
      </v-card>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          color="grey"
          text
          @click="modalVisible = false"
        >
          Close
        </v-btn>
        <v-btn
          color="primary"
          @click="editDialog = true"
        >
          <v-icon
            left
            small
          >
            fa fa-pen
          </v-icon>
          Edit
        </v-btn>
      </v-card-actions>
    </template>

    <edit-marketing-campaign-dialog
      v-if="playlist"
      :client="playlist.client"
      :playlist="playlist"
      :visible.sync="editDialog"
      @playlist:updated="fetchPlaylist(); $emit('playlist:updated', $event)"
      @playlist:deleted="$emit('playlist:deleted', $event); playlist = undefined; modalVisible = false"
    />
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import Playlists from '@/services/Playlists'
import DateRangeLabel from '@/components/helpers/DateRangeLabel'
import EditMarketingCampaignDialog from '@/components/marketing/EditMarketingCampaignDialog'

export default {
  name: 'ViewMarketingCampaignDialog',
  components: { EditMarketingCampaignDialog, DateRangeLabel, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    playlistId: {
      type: Number,
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      playlist: undefined,
      editDialog: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    }
  },
  watch: {
    playlistId() {
      this.fetchPlaylist()
    }
  },
  created() {
    this.fetchPlaylist()
  },
  methods: {
    async fetchPlaylist() {
      try {
        this.loading = true

        this.playlist = await Playlists.get(this.playlistId)
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Error fetching marketing campaign',
          color: 'error'
        })
        throw e
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
